import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail, Search, Send, User, Users, ChevronLeft } from 'lucide-react';
import { findUserByEmail, sendUserMessage, fetchApprovedTesters, FoundUser } from '../../../services/firebaseService';

type Mode = 'individual' | 'broadcast';

const AdminMessagesPanel: React.FC = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('individual');

  // Individual mode state
  const [emailQuery, setEmailQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [foundUser, setFoundUser] = useState<FoundUser | null>(null);

  // Shared compose state
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [broadcastCount, setBroadcastCount] = useState<number | null>(null);

  const handleSearch = async () => {
    if (!emailQuery.trim()) return;
    setSearching(true);
    setSearchError(null);
    setFoundUser(null);
    setSent(false);
    try {
      const user = await findUserByEmail(emailQuery.trim());
      setFoundUser(user);
    } catch (e: any) {
      setSearchError(e.message || t('admin.error_no_user_found', 'Ingen bruker funnet med den e-postadressen.'));
    } finally {
      setSearching(false);
    }
  };

  const handleSend = async () => {
    if (!foundUser || !subject.trim() || !body.trim()) return;
    setSending(true);
    setSendError(null);
    try {
      await sendUserMessage({ userId: foundUser.uid, subject: subject.trim(), body: body.trim(), sendEmail });
      setSent(true);
      setSubject('');
      setBody('');
    } catch (e: any) {
      setSendError(e.message || 'Sending feilet.');
    } finally {
      setSending(false);
    }
  };

  const handleBroadcast = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    setSendError(null);
    setSent(false);
    setBroadcastCount(null);
    try {
      const testers = await fetchApprovedTesters();
      const results = await Promise.allSettled(
        testers.map(t => sendUserMessage({ userId: t.uid, subject: subject.trim(), body: body.trim(), sendEmail }))
      );
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;
      setBroadcastCount(succeeded);
      if (failed > 0) setSendError(t('admin.broadcast_failed_count', '{{count}} meldinger feilet.', { count: failed }));
      setSent(true);
      setSubject('');
      setBody('');
    } catch (e: any) {
      setSendError(e.message || 'Sending feilet.');
    } finally {
      setSending(false);
    }
  };

  const resetCompose = () => {
    setSubject('');
    setBody('');
    setSent(false);
    setSendError(null);
    setBroadcastCount(null);
    setFoundUser(null);
    setEmailQuery('');
    setSearchError(null);
  };

  return (
    <div className="space-y-5 w-full pb-6">

      {/* Mode toggle */}
      <div className="flex gap-2">
        <button
          onClick={() => { setMode('individual'); resetCompose(); }}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${mode === 'individual' ? 'bg-sky-600 text-white' : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface)]'}`}
        >
          <User size={13} /> {t('admin.mode_individual', 'Individuell')}
        </button>
        <button
          onClick={() => { setMode('broadcast'); resetCompose(); }}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${mode === 'broadcast' ? 'bg-amber-600 text-white' : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface)]'}`}
        >
          <Users size={13} /> {t('admin.mode_broadcast', 'Alle testere')}
        </button>
      </div>

      {/* Individual: user search */}
      {mode === 'individual' && (
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl p-5 space-y-3">
          <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('admin.find_recipient', 'Finn mottaker')}</p>
          <div className="flex gap-2">
            <input
              type="email"
              placeholder={t('admin.email_placeholder', 'bruker@epost.no')}
              value={emailQuery}
              onChange={e => setEmailQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="flex-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-4 py-2.5 text-[var(--text-primary)] text-sm focus:border-sky-500/50 outline-none"
            />
            <button
              type="button"
              onClick={handleSearch}
              disabled={searching || !emailQuery.trim()}
              className="px-4 py-2.5 bg-sky-600/20 hover:bg-sky-600/30 border border-sky-500/30 text-sky-300 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
            >
              {searching ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
              {t('common.search', 'Søk')}
            </button>
          </div>

          {searchError && <p className="text-tiny text-red-400">{searchError}</p>}

          {foundUser && (
            <div className="flex items-center gap-3 p-3 bg-sky-500/10 border border-sky-500/20 rounded-xl">
              {foundUser.photoURL
                ? <img src={foundUser.photoURL} alt="" className="w-9 h-9 rounded-full object-cover" />
                : <div className="w-9 h-9 rounded-full bg-[var(--bg-surface)] flex items-center justify-center"><User size={16} className="text-[var(--text-secondary)]" /></div>
              }
              <div>
                <p className="text-sm font-bold text-[var(--text-primary)]">{foundUser.displayName || t('admin.no_name', '(ingen navn)')}</p>
                <p className="text-2xs text-[var(--text-secondary)]">{foundUser.email}{foundUser.isTester ? ` · ${t('admin.tester_label', 'Tester')}` : ''}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Broadcast info */}
      {mode === 'broadcast' && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4 flex items-center gap-3">
          <Users size={20} className="text-amber-400 shrink-0" />
          <div>
            <p className="text-xs font-black text-amber-300">{t('admin.broadcast_title', 'Melding til alle testere')}</p>
            <p className="text-2xs text-[var(--text-secondary)] mt-0.5">{t('admin.broadcast_subtitle', 'Sendes til alle brukere med aktiv testerstatus i systemet.')}</p>
          </div>
        </div>
      )}

      {/* Composer — shown when individual+user found, or broadcast mode */}
      {(mode === 'broadcast' || foundUser) && (
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl p-5 space-y-4">
          <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('common.message', 'Melding')}</p>

          <div className="space-y-1">
            <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t('common.subject', 'Emne')}</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder={t('admin.subject_placeholder', 'Emne for meldingen')}
              className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-4 py-2.5 text-[var(--text-primary)] text-sm focus:border-sky-500/50 outline-none"
            />
          </div>

          <div className="space-y-1">
            <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t('common.content', 'Innhold')}</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder={t('admin.message_placeholder', 'Skriv meldingen her…')}
              rows={8}
              className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl px-4 py-3 text-[var(--text-primary)] text-sm focus:border-sky-500/50 outline-none resize-y leading-relaxed min-h-[160px]"
            />
            <p className="text-3xs text-[var(--text-muted)]">{t('admin.message_help', 'Linjeskift beholdes. Teksten vises i appen og (valgfritt) i e-post.')}</p>
          </div>

          {/* E-post toggle */}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={e => setSendEmail(e.target.checked)}
              className="w-4 h-4 rounded accent-sky-500"
            />
            <div>
              <p className="text-tiny font-bold text-[var(--text-primary)]">{t('admin.send_as_email', 'Send også som e-post')}</p>
              <p className="text-3xs text-[var(--text-muted)]">{t('admin.email_sender_info', 'Sendes fra post@fricamp.com via Brevo')}</p>
            </div>
            <Mail size={14} className="text-sky-400 ml-auto" />
          </label>

          {sendError && <p className="text-tiny text-red-400">{sendError}</p>}

          {sent && (
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
              <p className="text-tiny text-emerald-300 font-bold">
                {mode === 'broadcast'
                  ? t('admin.broadcast_sent', 'Sendt til {{count}} testere!', { count: broadcastCount })
                  : t('admin.message_sent', 'Melding sendt!')
                }
              </p>
            </div>
          )}

          <button
            type="button"
            onClick={mode === 'broadcast' ? handleBroadcast : handleSend}
            disabled={sending || !subject.trim() || !body.trim()}
            className={`w-full disabled:opacity-40 text-white py-3 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95 ${mode === 'broadcast' ? 'bg-amber-600 hover:bg-amber-500' : 'bg-sky-600 hover:bg-sky-500'}`}
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {mode === 'broadcast'
              ? t('admin.broadcast_send_btn', 'Send til alle testere')
              : t('admin.send_to_recipient', 'Send til {{name}}', { name: foundUser?.displayName || foundUser?.email || t('common.recipient', 'mottaker') })
            }
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminMessagesPanel;
